import { Box, Button, DialogActions, Divider, IconButton, useMediaQuery, useTheme } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useFormikContext } from 'formik';
import { ERPJobFormValues } from '@/raptorjob/presentation/ERPJobValidationSchema';
import tid from '@/common/infrastructure/config/tid';
import { validateResourcesForm } from '@/raptorjob/presentation/pages/JobTicketOverviewDetailsPage/utils/validateResourcesForm';
import { useMemo } from 'react';
import type { CrewField } from '../RaptorJobCreateModal';
import { JOB_FORM_MODES, JOB_FORM_STEPS, type JobFormMode, type JobFormStepType } from '@/raptorjob/domain/constants/jobFormLabels';

interface ERPCreateModalDialogActionsProps {
  step: JobFormStepType;
  setStep: (step: JobFormStepType) => void;
  handleClose: () => void;
  onSubmit?: () => Promise<void>;
  mode?: JobFormMode;
  crewFields?: CrewField[];
  selectedSize?: { id?: string | number | null; name?: string | null } | null;
  jobEndDateActive?: boolean;
}

export const ERPCreateModalDialogActions = ({
  step,
  setStep,
  handleClose,
  onSubmit,
  mode = JOB_FORM_MODES.CREATE,
  crewFields = [],
  selectedSize = null,
  jobEndDateActive = false
}: ERPCreateModalDialogActionsProps) => {
  const { isValid, dirty, isSubmitting, submitForm, validateForm, values, errors } = useFormikContext<ERPJobFormValues>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isSizeSelected = values.equipmentSizeId != null;
  const shouldDisableCreateJob = step === JOB_FORM_STEPS.RESOURCES && !isSizeSelected;

  // Check if only equipmentSizeId has an error (it's only required for RESOURCES step)
  const onlyEquipmentSizeIdError = errors && Object.keys(errors).length === 1 && 'equipmentSizeId' in errors;

  // Validate resources form when on RESOURCES step
  const resourcesValidation = useMemo(() => {
    if (step === JOB_FORM_STEPS.RESOURCES) {
      return validateResourcesForm(selectedSize, crewFields);
    }
    return { isValid: true, errors: {} };
  }, [step, selectedSize, crewFields]);

  // Combined validation: Formik validation + resources validation
  const isFormValid = isValid && resourcesValidation.isValid;

  // Check if endDate is required (jobEndDateActive is true) but invalid or missing
  const isEndDateRequiredButInvalid = Boolean(jobEndDateActive && (errors?.endDate || !values.endDate));

  // Edit mode: Show only CANCEL and SAVE buttons
  if (mode === JOB_FORM_MODES.EDIT) {
    const handleSaveClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      const validationErrors = await validateForm();
      if (Object.keys(validationErrors).length > 0) {
        // Fall back to submitForm to mark fields as touched and show validation errors
        await submitForm();
        return;
      }
      // Form is valid — call the custom interceptor (which may show a warning dialog)
      await onSubmit?.();
    };

    return (
      <DialogActions sx={{ padding: 0 }}>
        <Box width={'100%'}>
          <Divider />
          <Box
            sx={{
              padding: { xs: 2, sm: '20px 24px' },
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <Button onClick={handleClose} disabled={isSubmitting} variant="text" {...tid('jobs:create:edit:cancel-button')}>
                CANCEL
              </Button>
              <Button
                variant="contained"
                disabled={!isFormValid || isSubmitting || isEndDateRequiredButInvalid}
                color="primary"
                type="button"
                onClick={handleSaveClick}
                loading={isSubmitting}
                {...tid('jobs:create:edit:save-button')}
              >
                SAVE
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogActions>
    );
  }

  // Create mode: Show CANCEL, CREATE JOB, and CONTINUE TO RESOURCES buttons
  return (
    <DialogActions sx={{ padding: 0 }}>
      <Box width={'100%'}>
        <Divider />
        <Box
          sx={{
            padding: { xs: 2, sm: '20px 24px' },
            display: 'flex',
            justifyContent: step === JOB_FORM_STEPS.JOB_INFO ? 'flex-end' : 'space-between'
          }}
        >
          {step === JOB_FORM_STEPS.RESOURCES && (
            <>
              {isMobile ? (
                <Box>
                  <IconButton
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => setStep(JOB_FORM_STEPS.JOB_INFO)}
                    color="primary"
                    {...tid('jobs:create:previous-button')}
                  >
                    <KeyboardArrowLeftIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  onClick={() => setStep(JOB_FORM_STEPS.JOB_INFO)}
                  disabled={isSubmitting}
                  variant="text"
                  startIcon={<KeyboardArrowLeftIcon />}
                  {...tid('jobs:create:previous-button')}
                >
                  PREVIOUS
                </Button>
              )}
            </>
          )}
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Button onClick={handleClose} disabled={isSubmitting} variant="text" {...tid('jobs:create:cancel-button')}>
              CANCEL
            </Button>
            {step === JOB_FORM_STEPS.JOB_INFO && (
              <Button
                variant="outlined"
                disabled={!isFormValid || !dirty || isSubmitting || isEndDateRequiredButInvalid}
                color="primary"
                type="button"
                onClick={onSubmit}
                loading={isSubmitting}
              >
                CREATE JOB
              </Button>
            )}
            {step === JOB_FORM_STEPS.JOB_INFO && (
              <Button
                variant="contained"
                color="primary"
                disabled={(!isFormValid && !onlyEquipmentSizeIdError) || !dirty || isSubmitting || isEndDateRequiredButInvalid}
                onClick={() => {
                  setStep(JOB_FORM_STEPS.RESOURCES);
                }}
                {...tid('jobs:create:continue-to-resources-button')}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  CONTINUE TO RESOURCES
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  CONTINUE
                </Box>
              </Button>
            )}
            {step === JOB_FORM_STEPS.RESOURCES && (
              <Button
                variant="contained"
                disabled={!isFormValid || !dirty || isSubmitting || shouldDisableCreateJob}
                color="primary"
                type="button"
                onClick={onSubmit}
                loading={isSubmitting}
                {...tid('jobs:create:create-job-button')}
              >
                CREATE JOB
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </DialogActions>
  );
};
